import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from './components/PageHeader';
import { apiFetch } from './utils/api';

const EMPTY_LIBRARY = {
  label: '',
  key: '',
  marker: 'DOC',
  description: '',
};

const FALLBACK_LIBRARIES = [
  { key: 'invoice', label: 'Invoices', marker: 'INV', description: 'Invoice document templates', is_enabled: true, is_system: true },
  { key: 'quote', label: 'Quotes', marker: 'QTE', description: 'Quote and estimate document templates', is_enabled: true, is_system: true },
  { key: 'prospect_quote', label: 'Prospect Quotes', marker: 'PQ', description: 'Prospect quote document templates', is_enabled: true, is_system: true },
  { key: 'ppm_quote', label: 'PPM Quotes', marker: 'PPMQ', description: 'PPM quote document templates', is_enabled: true, is_system: true },
  { key: 'job_sheet', label: 'Job Sheets', marker: 'JOB', description: 'Job sheet document templates', is_enabled: true, is_system: true },
  { key: 'purchase_order', label: 'Purchase Orders', marker: 'PO', description: 'Purchase order document templates', is_enabled: true, is_system: true },
  { key: 'risk_assessment', label: 'Risk Assessments', marker: 'RAMS', description: 'Risk assessment document templates', is_enabled: true, is_system: true },
  { key: 'service_report', label: 'Service Reports', marker: 'SRV', description: 'Service report document templates', is_enabled: true, is_system: true },
  { key: 'completion_certificate', label: 'Completion Certificates', marker: 'CERT', description: 'Completion certificate document templates', is_enabled: true, is_system: true },
  { key: 'delivery_note', label: 'Delivery Notes', marker: 'DN', description: 'Delivery note document templates', is_enabled: true, is_system: true },
  { key: 'sales_order', label: 'Sales Orders', marker: 'SO', description: 'Sales order document templates', is_enabled: true, is_system: true },
  { key: 'ppm_contract', label: 'PPM Contracts', marker: 'PPM', description: 'PPM contract document templates', is_enabled: true, is_system: true },
  { key: 'compliance_forms', label: 'Compliance Forms', marker: 'COMP', description: 'Compliance form question lists and PDF templates', is_enabled: true, is_system: true },
];

function DocumentTemplateLibrariesSetup() {
  const [libraries, setLibraries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newLibrary, setNewLibrary] = useState(EMPTY_LIBRARY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [templateCountError, setTemplateCountError] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editLibrary, setEditLibrary] = useState(EMPTY_LIBRARY);

  useEffect(() => {
    loadData();
  }, []);

  const typeCounts = useMemo(() => {
    return templates.reduce((acc, template) => {
      const type = template.document_type || 'other';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
  }, [templates]);
  const librarySetupAvailable = libraries.length > 0 && libraries.every((library) => library.id);

  async function loadData() {
    setLoading(true);
    setError(null);
    setTemplateCountError(null);
    try {
      const librariesRes = await apiFetch('/api/documents/libraries');
      const librariesData = await librariesRes.json();
      setLibraries(Array.isArray(librariesData) ? librariesData : FALLBACK_LIBRARIES);
    } catch (err) {
      setLibraries(FALLBACK_LIBRARIES);
      setError(`${err.message || 'Failed to load document library setup.'} Showing default libraries until the backend responds.`);
    }

    try {
      const templatesRes = await apiFetch('/api/documents/templates');
      const templatesData = await templatesRes.json();
      setTemplates(Array.isArray(templatesData) ? templatesData : []);
    } catch (err) {
      setTemplates([]);
      setTemplateCountError(err.message || 'Template counts could not be loaded.');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleLibrary(library) {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/documents/libraries/${library.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: library.is_enabled === false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update library.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update library.');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateLibrary(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch('/api/documents/libraries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: newLibrary.label,
          key: newLibrary.key,
          marker: newLibrary.marker,
          description: newLibrary.description,
          is_enabled: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create library.');
      setNewLibrary(EMPTY_LIBRARY);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to create library.');
    } finally {
      setSaving(false);
    }
  }

  function startEditLibrary(library) {
    setEditingKey(library.key);
    setEditLibrary({
      label: library.label || '',
      key: library.key || '',
      marker: library.marker || 'DOC',
      description: library.description || '',
    });
  }

  function cancelEditLibrary() {
    setEditingKey(null);
    setEditLibrary(EMPTY_LIBRARY);
  }

  async function handleUpdateLibrary(library) {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/documents/libraries/${library.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: editLibrary.label,
          marker: editLibrary.marker,
          description: editLibrary.description,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update library.');
      cancelEditLibrary();
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update library.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteLibrary(library) {
    const count = typeCounts[library.key] || 0;
    if (library.is_system) {
      setError('System libraries cannot be deleted.');
      return;
    }
    if (count > 0) {
      setError('Delete or move templates in this library first.');
      return;
    }
    if (!window.confirm(`Delete the ${library.label} library?`)) return;

    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/documents/libraries/${library.key}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete library.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete library.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Document Library Setup"
        subtitle="Choose which document template libraries are available"
        backTo="/settings/operations-files"
        backLabel="Operations Setup"
        sticky
      />

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-4">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-semibold">Close</button>
          </div>
        )}

        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Available Libraries</h2>
              <p className="text-sm text-gray-500 mt-1">
                Enabled libraries appear in Document Templates and future module template selectors.
              </p>
            </div>
            <button
              type="button"
              onClick={loadData}
              disabled={loading || saving}
              className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-sm text-gray-700"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading libraries...</div>
          ) : (
            <>
              {templateCountError && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-4 text-sm">
                  {templateCountError}
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-3">
                {libraries.map((library) => {
                  const enabled = library.is_enabled !== false;
                  const count = typeCounts[library.key] || 0;
                  const isEditing = editingKey === library.key;
                  return (
                    <div key={library.key} className="border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="w-11 h-11 rounded-lg bg-[#082547] text-white flex items-center justify-center text-xs font-bold">
                            {isEditing ? editLibrary.marker || 'DOC' : library.marker || 'DOC'}
                          </span>
                          {isEditing ? (
                            <div className="min-w-0 grid sm:grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={editLibrary.label}
                                onChange={(event) => setEditLibrary((prev) => ({ ...prev, label: event.target.value }))}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
                                placeholder="Library name"
                              />
                              <input
                                type="text"
                                value={editLibrary.marker}
                                onChange={(event) => setEditLibrary((prev) => ({ ...prev, marker: event.target.value.toUpperCase().slice(0, 12) }))}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
                                placeholder="Marker"
                              />
                              <input
                                type="text"
                                value={editLibrary.description}
                                onChange={(event) => setEditLibrary((prev) => ({ ...prev, description: event.target.value }))}
                                className="sm:col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
                                placeholder="Description"
                              />
                            </div>
                          ) : (
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-900 truncate">{library.label}</h3>
                                {library.is_system && <span className="text-xs text-gray-400">system</span>}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{library.description || 'Document template library'}</p>
                              <p className="text-xs text-gray-400 mt-1">{count} templates</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-end gap-2 shrink-0">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              onClick={() => handleUpdateLibrary(library)}
                              disabled={saving || !editLibrary.label.trim()}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#082547] text-white hover:bg-[#0a2d5c] disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditLibrary}
                              disabled={saving}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleToggleLibrary(library)}
                              disabled={saving || !library.id}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${
                                enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                              title={!library.id ? 'Backend library setup is not available yet' : ''}
                            >
                              {enabled ? 'Enabled' : 'Disabled'}
                            </button>
                            <button
                              type="button"
                              onClick={() => startEditLibrary(library)}
                              disabled={saving || !library.id}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteLibrary(library)}
                              disabled={saving || !library.id || library.is_system || count > 0}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-40"
                              title={library.is_system ? 'System libraries cannot be deleted' : count > 0 ? 'Delete or move templates first' : ''}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Library</h2>
          <form onSubmit={handleCreateLibrary} className="grid md:grid-cols-4 gap-3">
            <input
              type="text"
              value={newLibrary.label}
              onChange={(event) => setNewLibrary((prev) => ({ ...prev, label: event.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
              placeholder="Library name"
              disabled={saving || !librarySetupAvailable}
              required
            />
            <input
              type="text"
              value={newLibrary.key}
              onChange={(event) => setNewLibrary((prev) => ({ ...prev, key: event.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
              placeholder="Optional key"
              disabled={saving || !librarySetupAvailable}
            />
            <input
              type="text"
              value={newLibrary.marker}
              onChange={(event) => setNewLibrary((prev) => ({ ...prev, marker: event.target.value.toUpperCase().slice(0, 12) }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
              placeholder="Marker"
              disabled={saving || !librarySetupAvailable}
            />
            <button
              type="submit"
              disabled={saving || !librarySetupAvailable}
              className="bg-[#082547] hover:bg-[#0a2d5c] disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Add Library
            </button>
            <input
              type="text"
              value={newLibrary.description}
              onChange={(event) => setNewLibrary((prev) => ({ ...prev, description: event.target.value }))}
              className="md:col-span-4 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
              placeholder="Description"
              disabled={saving || !librarySetupAvailable}
            />
          </form>
        </section>
      </main>
    </div>
  );
}

export default DocumentTemplateLibrariesSetup;
